import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can, canGrant, ROLE_LABELS, ASSIGNABLE_ROLES, PERM_LABELS, PERMISSIONS } from "@/lib/permissions";
import { useSeo } from "@/lib/useSeo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, Download, LogOut, Star, Inbox, Users, MessageSquare, Settings, Eye, Upload, Trash2,
  UserCog, CalendarClock, LayoutDashboard, Paperclip, KeyRound, Plus, MessageCircle, Clock, AlertTriangle, MapPin, History, RotateCcw, Menu, Globe, MailCheck, Archive, ArchiveRestore, Building2 } from "lucide-react";
import { useConfig, settingsCompletion } from "@/context/ConfigContext";
import { ItineraryBuilder } from "@/components/site/ItineraryBuilder";
import { ManualEnquiryDialog } from "@/components/admin/ManualEnquiryDialog";
import { PartnerContacts, ConvertContactDialog } from "@/components/admin/CrmExtras";
import { PlacesManager } from "@/components/admin/PlacesManager";
import { CMSManager } from "@/components/admin/CMSManager";
import { SocialLinksEditor } from "@/components/admin/SocialLinksEditor";
import { PlacesProvider } from "@/components/admin/PlaceInputs";

// Vertical admin navigation. Each item: value, label, icon, required permission (null = always).
const NAV_ITEMS = [
  { value: "overview", label: "Dashboard", Icon: LayoutDashboard, perm: null },
  { value: "enquiries", label: "Enquiries", Icon: Inbox, perm: "view_crm" },
  { value: "partners", label: "Partners", Icon: Users, perm: "view_crm", hideForOps: true },
  { value: "followups", label: "Follow-ups", Icon: CalendarClock, perm: "view_crm" },
  { value: "contacts", label: "Contact Requests", Icon: MessageSquare, perm: "view_crm" },
  { value: "service-providers", label: "Service Providers", Icon: Building2, perm: "view_crm", hideForOps: true },
  { value: "itineraries", label: "Itineraries", Icon: Star, perm: "manage_content" },
  { value: "places", label: "Places", Icon: MapPin, perm: "manage_content" },
  { value: "cms", label: "Website CMS", Icon: Globe, perm: "manage_content" },
  { value: "testimonials", label: "Testimonials", Icon: MessageCircle, perm: "manage_content" },
  { value: "users", label: "Users", Icon: UserCog, perm: "manage_users" },
  { value: "settings", label: "Settings", Icon: Settings, perm: "manage_settings" },
];

const ENQUIRY_STATUSES = ["Incomplete", "New", "Contacted", "Requirement Understood", "Proposal In Progress", "Proposal Shared", "Follow-up", "Confirmed", "Closed", "Lost"];
const PARTNER_STATUSES = ["New Registration", "Verification Pending", "Contacted", "Approved", "Active Partner", "Inactive"];
const PROVIDER_STATUSES = ["New", "Contact Attempted", "Connected", "Under Evaluation", "Documents Requested", "Rate Contracting", "Approved for Onboarding", "Not Suitable", "On Hold", "Closed"];
const PROVIDER_TYPE_OPTIONS = [["hotel", "Hotel"], ["resort", "Resort"], ["homestay", "Homestay"], ["villa", "Villa"], ["activity", "Activity / Experience"], ["transport", "Transport"], ["tour_guide", "Tour Guide"], ["other", "Other"]];
const ROLES = ASSIGNABLE_ROLES;
const SOURCES = ["Website - Send Your Query", "Website - Partner Registration"];

const STATUS_COLOR = (s) => {
  if (["New", "New Registration"].includes(s)) return "bg-saffron/15 text-saffron";
  if (["Confirmed", "Approved", "Active Partner"].includes(s)) return "bg-forest/15 text-forest";
  if (["Lost", "Inactive", "Closed"].includes(s)) return "bg-maroon/15 text-maroon";
  return "bg-gold/20 text-charcoal";
};

function Stat({ label, value, icon: Icon, accent }) {
  return (
    <div className="rounded-xl border border-border bg-white p-5">
      <div className="flex items-center justify-between">
        <span className="font-accent text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
        <Icon className={`h-4 w-4 ${accent || "text-saffron"}`} />
      </div>
      <div className="mt-2 font-display text-3xl font-bold text-charcoal">{value}</div>
    </div>
  );
}

export default function AdminDashboard() {
  useSeo({ title: "Maha Sampada OS | Admin", noindex: true });
  const { user, logout } = useAuth();
  const cfg = useConfig();
  const logo = cfg.logo_url ? fileUrl(cfg.logo_url) : null;
  const navigate = useNavigate();
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "overview");
  const [navOpen, setNavOpen] = useState(false);
  const [dash, setDash] = useState({});
  const [assignables, setAssignables] = useState([]);

  const [enquiries, setEnquiries] = useState([]);
  const [partners, setPartners] = useState([]);
  const [providers, setProviders] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [itineraries, setItineraries] = useState([]);
  const [users, setUsers] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [config, setConfig] = useState(null);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all");

  const [detail, setDetail] = useState(null);
  const [detailKind, setDetailKind] = useState("enquiry");
  const [noteText, setNoteText] = useState("");
  const [showEnquiryForm, setShowEnquiryForm] = useState(false);
  const [editEnquiry, setEditEnquiry] = useState(null);
  const [convertContact, setConvertContact] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleteText, setDeleteText] = useState("");

  const loadDash = useCallback(() => { api.get("/admin/dashboard").then((r) => setDash(r.data)).catch(() => {}); }, []);
  const loadEnquiries = useCallback(() => {
    api.get("/admin/enquiries", { params: { status: statusFilter, search, owner: ownerFilter, date_from: dateFrom, date_to: dateTo, archived: showArchived } }).then((r) => setEnquiries(r.data));
  }, [statusFilter, search, ownerFilter, dateFrom, dateTo, showArchived]);
  const loadPartners = useCallback(() => {
    api.get("/admin/partners", { params: { status: statusFilter, search, owner: ownerFilter, date_from: dateFrom, date_to: dateTo, archived: showArchived } }).then((r) => setPartners(r.data));
  }, [statusFilter, search, ownerFilter, dateFrom, dateTo, showArchived]);
  const loadProviders = useCallback(() => {
    api.get("/admin/service-providers", { params: { status: statusFilter, search, owner: ownerFilter, provider_type: typeFilter, date_from: dateFrom, date_to: dateTo, archived: showArchived } }).then((r) => setProviders(r.data));
  }, [statusFilter, search, ownerFilter, typeFilter, dateFrom, dateTo, showArchived]);

  useEffect(() => {
    loadDash();
    api.get("/admin/assignable-users").then((r) => setAssignables(r.data)).catch(() => {});
  }, [loadDash]);

  useEffect(() => {
    if (tab === "enquiries") loadEnquiries();
    if (tab === "partners") loadPartners();
    if (tab === "service-providers") loadProviders();
    if (tab === "followups") api.get("/admin/followups").then((r) => setFollowups(r.data));
    if (tab === "contacts") api.get("/admin/contacts").then((r) => setContacts(r.data));
    if (tab === "itineraries") api.get("/itineraries").then((r) => setItineraries(r.data));
    if (tab === "users") api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    if (tab === "testimonials") api.get("/admin/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    if (tab === "settings") api.get("/config").then((r) => setConfig(r.data));
  }, [tab, loadEnquiries, loadPartners, loadProviders]);

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  const openDetail = async (id, kind) => {
    setDetailKind(kind); setNoteText("");
    const { data } = await api.get(`/admin/lead/${kind}/${id}`);
    // merge website form attachment
    if (data.file_id && !data.attachments.some((a) => a.id === data.file_id)) {
      data.attachments = [{ id: data.file_id, filename: "Website form attachment", size: 0 }, ...data.attachments];
    }
    setDetail(data);
  };
  const refreshDetail = async () => { if (detail) await openDetail(detail.id, detailKind); };
  const afterMutate = () => { detailKind === "enquiry" ? loadEnquiries() : detailKind === "provider" ? loadProviders() : loadPartners(); loadDash(); };

  const setStatus = async (status) => {
    try { await api.patch(`/admin/${detailKind}/${detail.id}/status`, { status }); toast.success("Status updated"); await refreshDetail(); afterMutate(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setAssign = async (uid) => {
    try { await api.patch(`/admin/${detailKind}/${detail.id}/assign`, { user_id: uid === "none" ? null : uid }); toast.success("Assignment updated"); await refreshDetail(); afterMutate(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveFollowup = async (date, note) => {
    try { await api.patch(`/admin/${detailKind}/${detail.id}/followup`, { date: date || null, note: note || "" }); toast.success("Follow-up saved"); await refreshDetail(); afterMutate(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addNote = async () => {
    if (!noteText.trim()) return;
    try { await api.post(`/admin/${detailKind}/${detail.id}/notes`, { text: noteText }); setNoteText(""); await refreshDetail(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const uploadAttachment = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/admin/${detailKind}/${detail.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("File uploaded"); await refreshDetail(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const viewFile = async (fid) => {
    try { const { data } = await api.get(`/admin/files/${fid}/sign`); window.open(fileUrl(data.url), "_blank"); }
    catch (e) { toast.error("Unable to open file"); }
  };
  const deleteFile = async (fid) => {
    try { await api.delete(`/admin/files/${fid}`); toast.success("File removed"); await refreshDetail(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const archiveLead = async () => {
    try { await api.patch(`/admin/${detailKind}/${detail.id}/archive`); toast.success("Record archived"); await refreshDetail(); afterMutate(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const restoreLead = async () => {
    try { await api.patch(`/admin/${detailKind}/${detail.id}/restore`); toast.success("Record restored"); await refreshDetail(); afterMutate(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doDeleteLead = async () => {
    try {
      await api.delete(`/admin/lead/${detailKind}/${confirmDelete.id}`, { data: { confirm: deleteText } });
      toast.success("Record permanently deleted");
      setConfirmDelete(null); setDeleteText(""); setDetail(null); afterMutate();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const exportCsv = async (kind) => {
    try {
      const { data } = await api.get(`/admin/${kind}/export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a"); a.href = url; a.download = `${kind}.csv`; a.click(); window.URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Export failed"); }
  };
  const toggleFeatured = async (slug) => {
    try { const { data } = await api.patch(`/admin/itineraries/${slug}/featured`); setItineraries((its) => its.map((i) => (i.slug === slug ? { ...i, featured: data.featured } : i))); }
    catch (e) { toast.error("Failed"); }
  };

  const resetFilters = () => { setSearch(""); setStatusFilter("all"); setOwnerFilter("all"); setDateFrom(""); setDateTo(""); setShowArchived(false); setTypeFilter("all"); };
  const changeTab = (v) => {
    setTab(v);
    const p = new URLSearchParams(window.location.search); p.set("tab", v);
    navigate({ search: `?${p.toString()}` }, { replace: false });
    resetFilters(); setNavOpen(false);
  };
  const navItems = NAV_ITEMS.filter((n) => (!n.perm || can(user, n.perm)) && !(n.hideForOps && user?.role === "operations"));
  const currentLabel = (navItems.find((n) => n.value === tab) || navItems[0] || {}).label || "Dashboard";
  // Sanitize the URL tab once the user (and thus permissions) are known: unknown/forbidden -> Dashboard.
  useEffect(() => {
    if (!user) return;
    const allowed = NAV_ITEMS.some((n) => n.value === tab && (!n.perm || can(user, n.perm)) && !(n.hideForOps && user?.role === "operations"));
    if (!allowed) {
      setTab("overview");
      const p = new URLSearchParams(window.location.search); p.set("tab", "overview");
      navigate({ search: `?${p.toString()}` }, { replace: true });
    }
  }, [user, tab, navigate]);
  const statuses = detailKind === "enquiry" ? ENQUIRY_STATUSES : detailKind === "provider" ? PROVIDER_STATUSES : PARTNER_STATUSES;

  return (
    <div className="min-h-screen bg-[#f4f4f2] lg:flex">
      {/* Mobile drawer backdrop */}
      {navOpen && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setNavOpen(false)} data-testid="admin-nav-backdrop" />}

      {/* Vertical sidebar — static on desktop, slide-out drawer on mobile/tablet */}
      <aside data-testid="admin-sidebar"
        className={`fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-border bg-white transition-transform duration-300 lg:sticky lg:top-0 lg:z-auto lg:h-screen lg:translate-x-0 ${navOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex items-center gap-3 border-b border-border px-5 py-4">
          {logo ? <img src={logo} alt="Maha Sampada" onError={(e) => { e.currentTarget.style.display = "none"; }} className="h-9 w-auto max-w-[130px] object-contain" /> : <div className="flex h-9 w-9 items-center justify-center rounded-full bg-maroon text-cream font-display font-bold">M</div>}
          <div><div className="font-display text-base font-bold leading-tight text-charcoal">Maha Sampada OS</div><div className="overline text-[8px] text-saffron">{ROLE_LABELS[user?.role] || "Admin"}</div></div>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4" data-testid="admin-sidebar-nav">
          {navItems.map((n) => {
            const Icon = n.Icon; const active = tab === n.value;
            return (
              <button key={n.value} onClick={() => changeTab(n.value)} data-testid={`nav-${n.value}`} aria-current={active ? "page" : undefined}
                className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left font-accent text-sm transition-colors ${active ? "bg-saffron text-white shadow-sm" : "text-charcoal hover:bg-saffron/10"}`}>
                <Icon className="h-4 w-4 shrink-0" />{n.label}
              </button>
            );
          })}
        </nav>
        <div className="border-t border-border px-4 py-3">
          <div className="mb-2 truncate font-accent text-xs text-muted-foreground" data-testid="sidebar-user">{user?.name}<br /><span className="text-[11px]">{user?.email}</span></div>
          <div className="flex items-center gap-2">
            <ChangePasswordButton />
            <Button size="sm" variant="outline" data-testid="logout-btn" onClick={doLogout} className="rounded-full text-xs"><LogOut className="mr-1 h-3.5 w-3.5" />Logout</Button>
          </div>
        </div>
      </aside>

      {/* Main column */}
      <div className="flex min-h-screen flex-1 flex-col min-w-0">
        <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-border bg-white px-4 py-3 sm:px-6">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setNavOpen(true)} data-testid="admin-nav-toggle" aria-label="Open navigation"><Menu className="h-5 w-5" /></Button>
          <h1 className="font-display text-lg font-bold text-charcoal" data-testid="admin-page-title">{currentLabel}</h1>
        </header>

        <main className="flex-1 p-4 sm:p-6">
        <Tabs value={tab}>

          {/* OVERVIEW */}
          <TabsContent value="overview" className="mt-4 space-y-4">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Stat label="Total Enquiries" value={dash.enquiries_total ?? 0} icon={Inbox} />
              <Stat label="New Enquiries" value={dash.enquiries_new ?? 0} icon={Inbox} accent="text-saffron" />
              <Stat label="Overdue Follow-ups" value={dash.overdue_followups ?? 0} icon={AlertTriangle} accent="text-maroon" />
              <Stat label="My Enquiries" value={dash.my_enquiries ?? 0} icon={UserCog} accent="text-forest" />
              <Stat label="New Today" value={dash.new_today ?? 0} icon={Clock} />
              <Stat label="Partner Registrations" value={dash.partners_total ?? 0} icon={Users} />
              <Stat label="New Partners" value={dash.partners_new ?? 0} icon={Users} accent="text-saffron" />
              <Stat label="Contact Messages" value={dash.contacts_total ?? 0} icon={MessageSquare} />
            </div>
            <div className="grid gap-4 lg:grid-cols-2">
              <div className="rounded-xl border border-border bg-white p-5">
                <h3 className="font-display text-lg font-semibold text-charcoal">Enquiry Pipeline</h3>
                <div className="mt-4 space-y-2">
                  {ENQUIRY_STATUSES.map((s) => {
                    const c = dash.enquiries_by_status?.[s] || 0;
                    const total = dash.enquiries_total || 1;
                    return (
                      <div key={s} className="flex items-center gap-3">
                        <span className="w-44 shrink-0 font-accent text-xs text-charcoal">{s}</span>
                        <div className="h-2 flex-1 overflow-hidden rounded-full bg-cream"><div className="h-full rounded-full bg-saffron" style={{ width: `${(c / total) * 100}%` }} /></div>
                        <span className="w-8 text-right font-accent text-xs font-semibold text-charcoal">{c}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="rounded-xl border border-border bg-white p-5">
                <h3 className="font-display text-lg font-semibold text-charcoal">Enquiries by Owner</h3>
                <div className="mt-4 space-y-2">
                  {(dash.enquiries_by_owner || []).map((o) => (
                    <div key={o.owner} className="flex items-center justify-between rounded-lg bg-cream px-4 py-2"><span className="font-accent text-sm text-charcoal">{o.owner}</span><Badge className="border-0 bg-forest/15 text-forest">{o.count}</Badge></div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* ENQUIRIES */}
          <TabsContent value="enquiries" className="mt-4">
            <div className="mb-3 flex justify-end">
              <Button data-testid="new-enquiry-btn" onClick={() => { setEditEnquiry(null); setShowEnquiryForm(true); }} className="rounded-full bg-saffron text-white hover:bg-saffron-dark"><Plus className="mr-1 h-4 w-4" />New Enquiry</Button>
            </div>
            <LeadFilters {...{ search, setSearch, statusFilter, setStatusFilter, statusOptions: ENQUIRY_STATUSES, ownerFilter, setOwnerFilter, assignables, dateFrom, setDateFrom, dateTo, setDateTo, resetFilters, showArchived, setShowArchived }}
              canExport={can(user, "export_data")} onExport={() => exportCsv("enquiries")} count={enquiries.length} searchTestId="enquiry-search" />
            <LeadTable rows={enquiries} cols={[["reference", "Ref"], ["agency_name", "Agency"], ["agent_name", "Agent"], ["destinations", "Destinations"]]} onView={(l) => openDetail(l.id, "enquiry")} empty="No enquiries match your filters." />
          </TabsContent>

          {/* PARTNERS */}
          {user?.role !== "operations" && (
            <TabsContent value="partners" className="mt-4">
              <LeadFilters {...{ search, setSearch, statusFilter, setStatusFilter, statusOptions: PARTNER_STATUSES, ownerFilter, setOwnerFilter, assignables, dateFrom, setDateFrom, dateTo, setDateTo, resetFilters, showArchived, setShowArchived }}
                canExport={can(user, "export_data")} onExport={() => exportCsv("partners")} count={partners.length} searchTestId="partner-search" />
              <LeadTable rows={partners} cols={[["agency_name", "Agency"], ["contact_person", "Contact"], ["city", "City"], ["business_type", "Type"]]} onView={(l) => openDetail(l.id, "partner")} empty="No partner registrations match your filters." />
            </TabsContent>
          )}

          {/* FOLLOWUPS */}
          <TabsContent value="followups" className="mt-4">
            <div className="overflow-x-auto rounded-xl border border-border bg-white">
              <Table>
                <TableHeader><TableRow><TableHead>Due</TableHead><TableHead>Ref</TableHead><TableHead>Agency</TableHead><TableHead>Status</TableHead><TableHead>Owner</TableHead><TableHead>Note</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {followups.length === 0 && <TableRow><TableCell colSpan={7} className="py-10 text-center text-muted-foreground">No upcoming follow-ups.</TableCell></TableRow>}
                  {followups.map((f) => (
                    <TableRow key={f.id} data-testid="followup-row">
                      <TableCell><Badge className={`border-0 ${f.overdue ? "bg-maroon/15 text-maroon" : "bg-forest/15 text-forest"}`}>{f.next_follow_up}{f.overdue ? " · overdue" : ""}</Badge></TableCell>
                      <TableCell className="font-medium">{f.reference}</TableCell><TableCell>{f.agency_name}</TableCell>
                      <TableCell><Badge className={`${STATUS_COLOR(f.status)} border-0`}>{f.status}</Badge></TableCell>
                      <TableCell className="text-xs">{f.assigned_to_name || "—"}</TableCell><TableCell className="max-w-xs truncate text-xs">{f.follow_up_note}</TableCell>
                      <TableCell><Button size="sm" variant="outline" className="rounded-full" onClick={() => openDetail(f.id, "enquiry")}><Eye className="h-4 w-4" /></Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* CONTACT REQUESTS */}
          <TabsContent value="contacts" className="mt-4">
            <div className="overflow-x-auto rounded-xl border border-border bg-white">
              <Table>
                <TableHeader><TableRow><TableHead>Ref</TableHead><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Category</TableHead><TableHead>Message</TableHead><TableHead>Status</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
                <TableBody>
                  {contacts.length === 0 && <TableRow><TableCell colSpan={8} className="py-10 text-center text-muted-foreground">No contact requests yet.</TableCell></TableRow>}
                  {contacts.map((c) => (
                    <TableRow key={c.id} data-testid="contact-row">
                      <TableCell className="whitespace-nowrap font-mono text-xs">{c.reference || "—"}</TableCell>
                      <TableCell className="font-medium">{c.name}</TableCell><TableCell>{c.email}</TableCell><TableCell>{c.category}</TableCell>
                      <TableCell className="max-w-xs truncate">{c.message}</TableCell>
                      <TableCell><Badge className={`${STATUS_COLOR(c.status === "Converted" ? "Confirmed" : "New")} border-0`}>{c.status === "Converted" ? "Converted" : "New"}</Badge></TableCell>
                      <TableCell className="whitespace-nowrap text-xs">{(c.created_at || "").slice(0, 10)}</TableCell>
                      <TableCell className="text-right">
                        {can(user, "manage_partners") && c.status !== "Converted" && (
                          <Button size="sm" variant="outline" data-testid={`convert-contact-${c.id}`} onClick={() => setConvertContact(c)} className="rounded-full text-xs">Convert</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* SERVICE PROVIDERS */}
          {user?.role !== "operations" && (
            <TabsContent value="service-providers" className="mt-4">
              <LeadFilters {...{ search, setSearch, statusFilter, setStatusFilter, statusOptions: PROVIDER_STATUSES, ownerFilter, setOwnerFilter, assignables, dateFrom, setDateFrom, dateTo, setDateTo, resetFilters, showArchived, setShowArchived, typeFilter, setTypeFilter, typeOptions: PROVIDER_TYPE_OPTIONS }}
                count={providers.length} searchTestId="provider-search" />
              <LeadTable rows={providers} cols={[["reference", "Ref"], ["business_name", "Business"], ["provider_type_label", "Type"], ["contact_person", "Contact"], ["city", "City"], ["district", "District"]]} onView={(l) => openDetail(l.id, "provider")} empty="No service provider registrations match your filters." />
            </TabsContent>
          )}

          {/* ITINERARIES */}
          {can(user, "manage_content") && (
            <TabsContent value="itineraries" className="mt-4">
              <ItineraryBuilder />
            </TabsContent>
          )}

          {/* PLACES */}
          {can(user, "manage_content") && (
            <TabsContent value="places" className="mt-4">
              <PlacesProvider><PlacesManager /></PlacesProvider>
            </TabsContent>
          )}

          {/* WEBSITE CMS */}
          {can(user, "manage_content") && (
            <TabsContent value="cms" className="mt-4">
              <CMSManager canPublish={can(user, "publish_content")} canEditLegal={["super_admin", "admin"].includes(user?.role)} onGoSettings={() => changeTab("settings")} />
            </TabsContent>
          )}

          {/* TESTIMONIALS */}
          {can(user, "manage_content") && <TabsContent value="testimonials" className="mt-4"><TestimonialsManager testimonials={testimonials} reload={() => api.get("/admin/testimonials").then((r) => setTestimonials(r.data))} /></TabsContent>}

          {/* USERS */}
          {can(user, "manage_users") && <TabsContent value="users" className="mt-4"><UsersManager users={users} me={user} reload={() => api.get("/admin/users").then((r) => setUsers(r.data))} /></TabsContent>}

          {/* SETTINGS */}
          {can(user, "manage_settings") && <TabsContent value="settings" className="mt-4">{config && <SettingsManager config={config} setConfig={setConfig} />}</TabsContent>}
        </Tabs>
        </main>
      </div>

      {/* LEAD DETAIL */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto" data-testid="lead-detail-dialog">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="font-display text-2xl">{detail.reference || detail.agency_name || detail.business_name}</DialogTitle></DialogHeader>
              <div className="flex flex-wrap items-center gap-2">
                <Badge className={`${STATUS_COLOR(detail.status)} border-0`}>{detail.status}</Badge>
                {detail.assigned_to_name && <Badge className="border-0 bg-forest/15 text-forest">Owner: {detail.assigned_to_name}</Badge>}
                {detail.next_follow_up && <Badge className="border-0 bg-gold/20 text-charcoal">Follow-up: {detail.next_follow_up}</Badge>}
                <span className="text-xs text-muted-foreground">Created {(detail.created_at || "").slice(0, 16).replace("T", " ")}</span>
                {detailKind === "enquiry" && can(user, "add_notes") && (
                  <Button size="sm" variant="outline" data-testid="edit-enquiry-btn" onClick={() => { setEditEnquiry(detail); setShowEnquiryForm(true); setDetail(null); }} className="ml-auto rounded-full text-xs">Edit Details</Button>
                )}
                <div className={`flex flex-wrap gap-1 ${detailKind === "enquiry" && can(user, "add_notes") ? "" : "ml-auto"}`}>
                  {detail.status !== "Archived" && can(user, "archive_records") && (
                    <Button size="sm" variant="outline" data-testid="archive-lead-btn" onClick={archiveLead} className="rounded-full text-xs"><Archive className="mr-1 h-3.5 w-3.5" />Archive</Button>
                  )}
                  {detail.status === "Archived" && can(user, "restore_records") && (
                    <Button size="sm" variant="outline" data-testid="restore-lead-btn" onClick={restoreLead} className="rounded-full text-xs"><ArchiveRestore className="mr-1 h-3.5 w-3.5" />Restore</Button>
                  )}
                  {detail.status === "Archived" && can(user, "delete_records") && (
                    <Button size="sm" variant="outline" data-testid="delete-lead-btn" onClick={() => { setConfirmDelete(detail); setDeleteText(""); }} className="rounded-full border-maroon/40 text-xs text-maroon hover:bg-maroon/10"><Trash2 className="mr-1 h-3.5 w-3.5" />Delete</Button>
                  )}
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                {can(user, "change_status") && (
                  <div><Label className="font-accent text-xs font-semibold">Status</Label>
                    <Select value={detail.status} onValueChange={setStatus}><SelectTrigger data-testid="update-status" className="mt-1"><SelectValue placeholder={detail.status || "Select status"} /></SelectTrigger><SelectContent>{[...new Set([detail.status, ...statuses])].filter(Boolean).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
                )}
                {can(user, "assign_leads") && (
                  <div><Label className="font-accent text-xs font-semibold">Assign To</Label>
                    <Select value={detail.assigned_to || "none"} onValueChange={setAssign}><SelectTrigger data-testid="assign-select" className="mt-1"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                      <SelectContent><SelectItem value="none">Unassigned</SelectItem>{assignables.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({ROLE_LABELS[u.role]})</SelectItem>)}</SelectContent></Select></div>
                )}
                {can(user, "manage_followups") && <FollowupControl detail={detail} onSave={saveFollowup} />}
              </div>

              <div className="grid gap-2 rounded-lg bg-[#f4f4f2] p-4 text-sm sm:grid-cols-2">
                {Object.entries(detail).filter(([k]) => !["_id", "id", "notes", "status", "lead_type", "lead_source", "created_at", "updated_at", "consent", "file_url", "file_id", "reference", "activities", "attachments", "assigned_to", "assigned_to_name", "next_follow_up", "follow_up_note", "tags", "contact_persons", "partner_id", "partner_contact_id", "created_by", "is_complete", "is_direct", "mobile_digits", "business_name_norm", "business_name", "provider_type", "conditional", "consent_at", "accuracy_confirmed", "source_url", "possible_duplicate", "duplicate_candidates", "prev_status", "archived_at", "archived_by"].includes(k))
                  .map(([k, v]) => (v !== "" && v !== null && v !== 0 && v !== false && typeof v !== "object" ? <div key={k}><span className="font-accent text-[11px] uppercase text-muted-foreground">{k.replace(/_/g, " ")}</span><div className="text-charcoal">{String(v)}</div></div> : null))}
              </div>

              {/* Service provider extras */}
              {detailKind === "provider" && (
                <div className="space-y-2">
                  {detail.possible_duplicate && <div className="rounded-lg border border-maroon/40 bg-maroon/10 px-4 py-2 text-xs font-medium text-maroon" data-testid="provider-duplicate-flag">Possible duplicate — a similar provider may already exist. Please review before proceeding.</div>}
                  {detail.conditional && Object.keys(detail.conditional).length > 0 && (
                    <div className="grid gap-2 rounded-lg border border-border bg-white p-4 text-sm sm:grid-cols-2" data-testid="provider-conditional">
                      {Object.entries(detail.conditional).map(([k, v]) => (
                        <div key={k}><span className="font-accent text-[11px] uppercase text-muted-foreground">{k.replace(/_/g, " ")}</span><div className="text-charcoal">{Array.isArray(v) ? v.join(", ") : String(v)}</div></div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Partner contact persons */}
              {detailKind === "partner" && (
                <div className="rounded-lg border border-border bg-white p-4">
                  <PartnerContacts partnerId={detail.id} canManage={can(user, "manage_partners")} />
                </div>
              )}

              {/* Attachments */}
              {can(user, "view_attachments") && (
                <div>
                  <div className="flex items-center justify-between"><Label className="font-accent text-xs font-semibold">Attachments (private)</Label>
                    {can(user, "upload_attachments") && <label className="flex cursor-pointer items-center gap-1 font-accent text-xs text-saffron hover:underline"><Upload className="h-3 w-3" />Upload<input type="file" data-testid="attachment-upload" className="hidden" onChange={(e) => uploadAttachment(e.target.files?.[0])} /></label>}
                  </div>
                  <div className="mt-2 space-y-1">
                    {detail.attachments?.length === 0 && <p className="text-xs text-muted-foreground">No attachments.</p>}
                    {detail.attachments?.map((a) => (
                      <div key={a.id} className="flex items-center justify-between rounded-lg border border-border bg-white px-3 py-2 text-sm">
                        <span className="flex items-center gap-2 truncate text-charcoal"><Paperclip className="h-3.5 w-3.5 text-muted-foreground" />{a.filename}</span>
                        <span className="flex items-center gap-2">
                          <button data-testid={`view-file-${a.id}`} onClick={() => viewFile(a.id)} className="font-accent text-xs text-saffron hover:underline">View</button>
                          {can(user, "upload_attachments") && a.size > 0 && <button onClick={() => deleteFile(a.id)} className="text-maroon hover:opacity-70"><Trash2 className="h-3.5 w-3.5" /></button>}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add note */}
              {can(user, "add_notes") && (
                <div className="flex gap-2">
                  <Textarea data-testid="note-input" rows={2} placeholder="Add an internal note…" value={noteText} onChange={(e) => setNoteText(e.target.value)} />
                  <Button onClick={addNote} data-testid="add-note-btn" className="rounded-full bg-forest text-white hover:bg-forest/90">Add</Button>
                </div>
              )}

              {/* Timeline */}
              <div>
                <Label className="font-accent text-xs font-semibold">Activity Timeline</Label>
                <div className="mt-2 space-y-2 border-l-2 border-gold/30 pl-4" data-testid="activity-timeline">
                  {detail.activities?.map((a) => (
                    <div key={a.id} className="relative">
                      <span className="absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full bg-saffron" />
                      <p className="text-sm text-charcoal">{a.message}</p>
                      <span className="text-[11px] text-muted-foreground">{a.author_name} · {(a.at || "").slice(0, 16).replace("T", " ")}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <ManualEnquiryDialog
        open={showEnquiryForm}
        editData={editEnquiry}
        assignables={assignables}
        onClose={() => { setShowEnquiryForm(false); setEditEnquiry(null); }}
        onSaved={() => { loadEnquiries(); loadDash(); }}
      />
      <ConvertContactDialog
        contact={convertContact}
        onClose={() => setConvertContact(null)}
        onConverted={() => { api.get("/admin/contacts").then((r) => setContacts(r.data)); loadDash(); }}
      />
      <Dialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) { setConfirmDelete(null); setDeleteText(""); } }}>
        <DialogContent data-testid="delete-lead-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl text-maroon">Permanently delete record</DialogTitle></DialogHeader>
          <p className="text-sm text-charcoal">This permanently deletes <strong>{confirmDelete?.reference || confirmDelete?.agency_name}</strong> and cannot be undone. It is blocked if any dependent quotations, proposals, bookings, payments or follow-ups exist.</p>
          <div className="space-y-1.5"><Label className="font-accent text-xs font-semibold">Type <span className="font-mono text-maroon">DELETE</span> to confirm</Label>
            <Input data-testid="delete-confirm-input" value={deleteText} onChange={(e) => setDeleteText(e.target.value)} placeholder="DELETE" /></div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setConfirmDelete(null); setDeleteText(""); }}>Cancel</Button>
            <Button data-testid="delete-confirm-btn" disabled={deleteText !== "DELETE"} onClick={doDeleteLead} className="rounded-full bg-maroon text-white hover:bg-maroon/90">Permanently Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FollowupControl({ detail, onSave }) {
  const [date, setDate] = useState(detail.next_follow_up || "");
  const [note, setNote] = useState(detail.follow_up_note || "");
  useEffect(() => { setDate(detail.next_follow_up || ""); setNote(detail.follow_up_note || ""); }, [detail.id]);
  return (
    <div><Label className="font-accent text-xs font-semibold">Follow-up Date</Label>
      <div className="mt-1 flex gap-1">
        <Input type="date" data-testid="followup-date" value={date} onChange={(e) => setDate(e.target.value)} className="text-xs" />
        <Button size="sm" data-testid="save-followup" onClick={() => onSave(date, note)} className="rounded-full bg-saffron text-white hover:bg-saffron-dark">Set</Button>
      </div>
      <Input data-testid="followup-note" placeholder="Follow-up note" value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 text-xs" />
    </div>
  );
}

function LeadFilters({ search, setSearch, statusFilter, setStatusFilter, statusOptions, ownerFilter, setOwnerFilter, assignables, dateFrom, setDateFrom, dateTo, setDateTo, resetFilters, canExport, onExport, count, searchTestId, showArchived, setShowArchived, typeFilter, setTypeFilter, typeOptions }) {
  return (
    <div className="mb-3 rounded-xl border border-border bg-white p-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" /><Input data-testid={searchTestId} className="pl-9" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} /></div>
        {typeOptions && (
          <Select value={typeFilter} onValueChange={setTypeFilter}><SelectTrigger className="w-44" data-testid="provider-type-filter"><SelectValue placeholder="Type" /></SelectTrigger><SelectContent><SelectItem value="all">All Types</SelectItem>{typeOptions.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
        )}
        <Select value={statusFilter} onValueChange={setStatusFilter}><SelectTrigger className="w-48" data-testid="status-filter"><SelectValue placeholder="Status" /></SelectTrigger><SelectContent><SelectItem value="all">All Statuses</SelectItem>{statusOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
        <Select value={ownerFilter} onValueChange={setOwnerFilter}><SelectTrigger className="w-44" data-testid="owner-filter"><SelectValue placeholder="Owner" /></SelectTrigger><SelectContent><SelectItem value="all">All Owners</SelectItem><SelectItem value="unassigned">Unassigned</SelectItem>{assignables.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent></Select>
        <Input type="date" data-testid="date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-36" />
        <Input type="date" data-testid="date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-36" />
        {setShowArchived && (
          <Button variant={showArchived ? "default" : "outline"} data-testid="toggle-archived" onClick={() => setShowArchived(!showArchived)} className={`rounded-full text-xs ${showArchived ? "bg-charcoal text-white" : ""}`}>
            <Archive className="mr-1 h-3.5 w-3.5" />{showArchived ? "Viewing Archived" : "Archived"}
          </Button>
        )}
        <Button variant="ghost" onClick={resetFilters} className="text-xs">Reset</Button>
        {canExport && <Button variant="outline" data-testid="export-btn" onClick={onExport} className="rounded-full"><Download className="mr-1 h-4 w-4" />Export</Button>}
      </div>
      <div className="mt-2 font-accent text-xs text-muted-foreground">{count} records{showArchived ? " · archived" : ""}</div>
    </div>
  );
}

function LeadTable({ rows, cols, onView, empty }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-white">
      <Table>
        <TableHeader><TableRow>{cols.map(([, label]) => <TableHead key={label}>{label}</TableHead>)}<TableHead>Owner</TableHead><TableHead>Status</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
        <TableBody>
          {rows.length === 0 && <TableRow><TableCell colSpan={cols.length + 4} className="py-10 text-center text-muted-foreground">{empty}</TableCell></TableRow>}
          {rows.map((r) => (
            <TableRow key={r.id} data-testid="lead-row">
              {cols.map(([k]) => <TableCell key={k} className="max-w-[180px] truncate font-medium">{r[k]}</TableCell>)}
              <TableCell className="whitespace-nowrap text-xs">{r.assigned_to_name || "—"}</TableCell>
              <TableCell><Badge className={`${STATUS_COLOR(r.status)} whitespace-nowrap border-0`}>{r.status}</Badge></TableCell>
              <TableCell className="whitespace-nowrap text-xs">{(r.created_at || "").slice(0, 10)}</TableCell>
              <TableCell className="text-right"><Button size="sm" variant="outline" data-testid={`view-lead-${r.id}`} onClick={() => onView(r)} className="rounded-full"><Eye className="h-4 w-4" /></Button></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function RoleMatrix() {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-border bg-cream/30 p-4" data-testid="role-matrix">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="toggle-role-matrix" className="flex w-full items-center justify-between font-accent text-sm font-bold uppercase tracking-wide text-maroon">
        Role &amp; Permission Matrix <span className="text-xs text-muted-foreground">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full border-collapse text-xs">
            <thead><tr><th className="sticky left-0 bg-cream/30 p-2 text-left">Permission</th>
              {ASSIGNABLE_ROLES.map((r) => <th key={r} className="p-2 text-center font-accent">{ROLE_LABELS[r]}</th>)}</tr></thead>
            <tbody>
              {Object.entries(PERM_LABELS).map(([perm, label]) => (
                <tr key={perm} className="border-t border-border">
                  <td className="sticky left-0 bg-white p-2 text-charcoal">{label}</td>
                  {ASSIGNABLE_ROLES.map((r) => (
                    <td key={r} className="p-2 text-center" data-testid={`matrix-${r}-${perm}`}>
                      {PERMISSIONS[r]?.includes(perm) ? <span className="text-forest">✓</span> : <span className="text-muted-foreground/40">—</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function RolePermSummary({ role }) {
  const perms = PERMISSIONS[role] || [];
  return (
    <div className="rounded-lg border border-border bg-cream/30 p-3" data-testid="role-perm-summary">
      <div className="font-accent text-[11px] font-semibold uppercase tracking-wide text-maroon">Permissions for {ROLE_LABELS[role] || role}</div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {perms.length === 0 ? <span className="text-xs text-muted-foreground">No permissions</span> :
          perms.map((p) => <span key={p} className="rounded-full bg-forest/10 px-2 py-0.5 text-[11px] text-forest">{PERM_LABELS[p] || p}</span>)}
      </div>
    </div>
  );
}

function UsersManager({ users, me, reload }) {
  const isSuper = me.role === "super_admin";
  const [form, setForm] = useState({ email: "", name: "", role: "operations", password: "", active: true });
  const [creating, setCreating] = useState(false);
  const [resetFor, setResetFor] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const [list, setList] = useState(users);
  const [delFor, setDelFor] = useState(null);
  const [delText, setDelText] = useState("");
  const [delReason, setDelReason] = useState("");
  useEffect(() => { if (!showDeleted) setList(users); }, [users, showDeleted]);
  const refetch = async (sd = showDeleted) => { const r = await api.get("/admin/users", { params: { include_deleted: sd } }); setList(r.data); };
  const roleOptions = ROLES.filter((r) => canGrant(me.role, r));
  const canManageTarget = (u) => !(u.role === "super_admin" && me.role !== "super_admin");
  const create = async () => {
    try { await api.post("/admin/users", form); toast.success("User created"); setForm({ email: "", name: "", role: "operations", password: "", active: true }); setCreating(false); refetch(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const update = async (id, patch) => { try { await api.put(`/admin/users/${id}`, patch); toast.success("User updated"); refetch(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const doReset = async () => { try { await api.put(`/admin/users/${resetFor.id}`, { password: newPw }); toast.success("Password reset"); setResetFor(null); setNewPw(""); refetch(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const sendResetLink = async (u) => { try { const r = await api.post(`/admin/users/${u.id}/reset-link`); toast.success(`Secure reset link sent to ${r.data.email}`); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const doDelete = async () => { try { await api.delete(`/admin/users/${delFor.id}`, { data: { confirm: delText, reason: delReason || null } }); toast.success("User deleted"); setDelFor(null); setDelText(""); setDelReason(""); refetch(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const doRestore = async (u) => { try { await api.post(`/admin/users/${u.id}/restore`); toast.success("User restored (inactive — send a reset link before they can sign in)"); refetch(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const toggleDeleted = () => { const nv = !showDeleted; setShowDeleted(nv); refetch(nv); };
  return (
    <div className="space-y-4">
      <RoleMatrix />
      <div className="flex items-center justify-end gap-2">
        {isSuper && <Button variant={showDeleted ? "default" : "outline"} data-testid="show-deleted-toggle" onClick={toggleDeleted} className={`rounded-full text-xs ${showDeleted ? "bg-charcoal text-white" : ""}`}>{showDeleted ? "Showing deleted" : "Show deleted users"}</Button>}
        <Button data-testid="new-user-btn" onClick={() => setCreating(true)} className="rounded-full bg-saffron text-white hover:bg-saffron-dark"><Plus className="mr-1 h-4 w-4" />New User</Button>
      </div>
      <div className="overflow-x-auto rounded-xl border border-border bg-white">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead>Last Login</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {list.map((u) => {
              const locked = !canManageTarget(u);
              const deleted = u.status === "deleted";
              return (
              <TableRow key={u.id} data-testid="user-row" className={deleted ? "opacity-60" : ""}>
                <TableCell>{deleted ? <span className="text-sm text-muted-foreground" data-testid={`deleted-name-${u.email}`}>Former user — {u.name}</span> : <Input defaultValue={u.name} data-testid={`name-input-${u.email}`} className="h-8 w-36" disabled={locked} onBlur={(e) => { if (e.target.value && e.target.value !== u.name) update(u.id, { name: e.target.value }); }} />}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>{deleted ? <span className="text-sm">{ROLE_LABELS[u.role] || u.role}</span> : <Select value={u.role} onValueChange={(v) => update(u.id, { role: v })} disabled={u.id === me.id || locked}><SelectTrigger className="w-44" data-testid={`role-select-${u.email}`}><SelectValue>{ROLE_LABELS[u.role] || u.role}</SelectValue></SelectTrigger><SelectContent>{(roleOptions.includes(u.role) ? roleOptions : [u.role, ...roleOptions]).map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent></Select>}</TableCell>
                <TableCell><Badge className={`border-0 ${deleted ? "bg-muted text-muted-foreground" : u.active === false ? "bg-maroon/15 text-maroon" : "bg-forest/15 text-forest"}`} data-testid={`status-${u.email}`}>{deleted ? "Deleted" : u.active === false ? "Inactive" : "Active"}</Badge></TableCell>
                <TableCell className="text-xs">{u.last_login ? u.last_login.slice(0, 10) : "—"}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {deleted ? (
                      isSuper && <Button size="sm" variant="outline" data-testid={`restore-user-${u.email}`} onClick={() => doRestore(u)} className="rounded-full text-xs"><ArchiveRestore className="mr-1 h-3.5 w-3.5" />Restore</Button>
                    ) : (<>
                      <Button size="sm" variant="outline" disabled={locked} data-testid={`reset-pw-${u.email}`} onClick={() => setResetFor(u)} className="rounded-full text-xs" title="Set password directly"><KeyRound className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="outline" disabled={locked || u.active === false} data-testid={`send-reset-link-${u.email}`} onClick={() => sendResetLink(u)} className="rounded-full text-xs" title="Email a secure reset link"><MailCheck className="h-3.5 w-3.5" /></Button>
                      {u.id !== me.id && <Button size="sm" variant="outline" disabled={locked} data-testid={`toggle-active-${u.email}`} onClick={() => update(u.id, { active: !(u.active !== false) })} className="rounded-full text-xs">{u.active === false ? "Activate" : "Deactivate"}</Button>}
                      {isSuper && u.id !== me.id && <Button size="sm" variant="outline" disabled={u.active !== false} title={u.active !== false ? "Deactivate the account before deleting" : "Delete user"} data-testid={`delete-user-${u.email}`} onClick={() => { setDelFor(u); setDelText(""); setDelReason(""); }} className="rounded-full border-maroon/40 text-xs text-maroon hover:bg-maroon/10"><Trash2 className="h-3.5 w-3.5" /></Button>}
                    </>)}
                  </div>
                </TableCell>
              </TableRow>
            ); })}
          </TableBody>
        </Table>
      </div>
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent data-testid="new-user-dialog"><DialogHeader><DialogTitle className="font-display text-xl">Create User</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-semibold">Name</Label><Input data-testid="new-user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label className="text-xs font-semibold">Email</Label><Input data-testid="new-user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label className="text-xs font-semibold">Role</Label><Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}><SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger><SelectContent>{roleOptions.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent></Select></div>
            <RolePermSummary role={form.role} />
            <div><Label className="text-xs font-semibold">Password (min 8 chars)</Label><PasswordInput testId="new-user-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="new-user-active" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Account active (can sign in)</label>
          </div>
          <DialogFooter><Button data-testid="create-user-submit" onClick={create} className="rounded-full bg-saffron text-white hover:bg-saffron-dark">Create User</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!resetFor} onOpenChange={(o) => !o && setResetFor(null)}>
        <DialogContent data-testid="reset-pw-dialog"><DialogHeader><DialogTitle className="font-display text-xl">Reset Access — {resetFor?.name}</DialogTitle></DialogHeader>
          <div><Label className="text-xs font-semibold">New Password (min 8 chars)</Label><PasswordInput testId="reset-pw-input" value={newPw} onChange={(e) => setNewPw(e.target.value)} /></div>
          <DialogFooter><Button data-testid="reset-pw-submit" onClick={doReset} className="rounded-full bg-saffron text-white hover:bg-saffron-dark">Reset Password</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!delFor} onOpenChange={(o) => { if (!o) { setDelFor(null); setDelText(""); setDelReason(""); } }}>
        <DialogContent data-testid="delete-user-dialog"><DialogHeader><DialogTitle className="font-display text-xl text-maroon">Delete user</DialogTitle></DialogHeader>
          <p className="text-sm text-charcoal">This will deactivate and tombstone <strong>{delFor?.name}</strong> (<strong>{delFor?.email}</strong>). Their work, records and audit history are preserved and remain attributed to them. The account can be restored later by a Super Admin.</p>
          <div className="space-y-2">
            <div><Label className="text-xs font-semibold">Reason (optional)</Label><Input data-testid="delete-user-reason" value={delReason} onChange={(e) => setDelReason(e.target.value)} placeholder="e.g. left the organisation" /></div>
            <div><Label className="text-xs font-semibold">Type <span className="font-mono text-maroon">DELETE</span> to confirm</Label><Input data-testid="delete-user-confirm" value={delText} onChange={(e) => setDelText(e.target.value)} placeholder="DELETE" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setDelFor(null); setDelText(""); setDelReason(""); }}>Cancel</Button>
            <Button data-testid="delete-user-submit" disabled={delText !== "DELETE"} onClick={doDelete} className="rounded-full bg-maroon text-white hover:bg-maroon/90">Delete User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TestimonialsManager({ testimonials, reload }) {
  const [editing, setEditing] = useState(null);
  const blank = { quote: "", author: "", location: "", active: true, featured: false, source_provider: "direct", source_url: "", rating: "", review_date: "", verified: false };
  const [form, setForm] = useState(blank);
  const PROVIDERS = ["google", "facebook", "trustpilot", "trustindex", "tripadvisor", "direct", "other"];
  const save = async () => {
    try {
      if (editing && editing !== "new") await api.put(`/admin/testimonials/${editing}`, form);
      else await api.post("/admin/testimonials", form);
      toast.success("Saved"); setEditing(null); setForm(blank); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { try { await api.delete(`/admin/testimonials/${id}`); toast.success("Deleted"); reload(); } catch (e) { toast.error("Failed"); } };
  return (
    <div className="space-y-4">
      <div className="flex justify-end"><Button data-testid="new-testimonial-btn" onClick={() => { setForm(blank); setEditing("new"); }} className="rounded-full bg-saffron text-white hover:bg-saffron-dark"><Plus className="mr-1 h-4 w-4" />Add Testimonial</Button></div>
      <div className="grid gap-3 sm:grid-cols-2">
        {testimonials.map((t) => (
          <div key={t.id} className="rounded-xl border border-border bg-white p-4" data-testid="testimonial-manage-card">
            <p className="text-sm italic text-charcoal">"{t.quote}"</p>
            <div className="mt-2 flex items-center justify-between"><div><div className="font-accent text-xs font-semibold">{t.author}</div><div className="text-[11px] text-muted-foreground">{t.location}</div></div>
              <div className="flex gap-2"><Badge className={`border-0 ${t.active ? "bg-forest/15 text-forest" : "bg-muted text-muted-foreground"}`}>{t.active ? "Live" : "Hidden"}</Badge>
                <button data-testid={`edit-testimonial-${t.id}`} onClick={() => { setForm({ quote: t.quote, author: t.author, location: t.location, active: t.active, featured: t.featured || false, source_provider: t.source_provider || "direct", source_url: t.source_url || "", rating: t.rating ?? "", review_date: t.review_date || "", verified: t.verified || false }); setEditing(t.id); }} className="text-xs text-saffron hover:underline">Edit</button>
                <button data-testid={`delete-testimonial-${t.id}`} onClick={() => del(t.id)} className="text-maroon"><Trash2 className="h-3.5 w-3.5" /></button></div>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="testimonial-dialog"><DialogHeader><DialogTitle className="font-display text-xl">{editing === "new" ? "Add" : "Edit"} Testimonial</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-semibold">Quote</Label><Textarea data-testid="testimonial-quote" rows={3} value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3"><div><Label className="text-xs font-semibold">Author</Label><Input data-testid="testimonial-author" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} /></div><div><Label className="text-xs font-semibold">Location</Label><Input data-testid="testimonial-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div></div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="testimonial-active" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Show on website</label>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold">Review source</Label>
                <select data-testid="testimonial-provider" value={form.source_provider} onChange={(e) => setForm({ ...form, source_provider: e.target.value })} className="mt-1 w-full rounded-md border border-input bg-white px-3 py-2 text-sm">
                  {PROVIDERS.map((p) => <option key={p} value={p}>{p === "direct" ? "Direct Client Feedback" : p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                </select></div>
              <div><Label className="text-xs font-semibold">Rating (0–5, optional)</Label><Input type="number" step="0.5" min="0" max="5" data-testid="testimonial-rating" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value === "" ? "" : parseFloat(e.target.value) })} /></div>
            </div>
            {form.source_provider !== "direct" && <div><Label className="text-xs font-semibold">Original review URL{form.source_provider === "other" ? " (any https)" : ""}</Label><Input data-testid="testimonial-source-url" value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })} placeholder="https://…" /></div>}
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold">Review date (optional)</Label><Input type="date" data-testid="testimonial-date" value={form.review_date} onChange={(e) => setForm({ ...form, review_date: e.target.value })} /></div>
              <label className="mt-6 flex items-center gap-2 text-sm"><input type="checkbox" data-testid="testimonial-verified" checked={form.verified} onChange={(e) => setForm({ ...form, verified: e.target.checked })} /> Verified source</label>
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="testimonial-featured" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Featured</label>
          </div>
          <DialogFooter><Button data-testid="save-testimonial" onClick={save} className="rounded-full bg-saffron text-white hover:bg-saffron-dark">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function NotificationRouting() {
  const FORMS = [["enquiry", "Travel Enquiry"], ["partner", "Travel Partner Registration"], ["contracting", "Contracting / Service Partner"], ["contact", "General Contact"]];
  const [r, setR] = useState(null);
  useEffect(() => { api.get("/admin/notification-routing").then((x) => setR(x.data)).catch(() => {}); }, []);
  const save = async () => { try { await api.put("/admin/notification-routing", r); toast.success("Notification routing saved"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  if (!r) return null;
  return (
    <div className="mt-8 rounded-lg border border-border bg-cream/30 p-4" data-testid="notification-routing">
      <Label className="font-accent text-sm font-semibold text-charcoal">Notification Routing</Label>
      <p className="mt-0.5 text-xs text-muted-foreground">Where each form's lead notification is sent, and the applicant acknowledgement Reply-To. Sender: Maha Sampada &lt;hello@mahasampada.com&gt;.</p>
      <div className="mt-3 space-y-2">
        {FORMS.map(([k, label]) => (
          <div key={k} className="grid gap-2 sm:grid-cols-[180px_1fr_1fr]" data-testid={`routing-row-${k}`}>
            <div className="flex items-center text-xs font-semibold text-charcoal">{label}</div>
            <Input value={r.recipients[k] || ""} onChange={(e) => setR({ ...r, recipients: { ...r.recipients, [k]: e.target.value } })} data-testid={`routing-recipient-${k}`} placeholder="recipient@…" />
            <Input value={r.reply_to[k] || ""} onChange={(e) => setR({ ...r, reply_to: { ...r.reply_to, [k]: e.target.value } })} data-testid={`routing-replyto-${k}`} placeholder="reply-to@…" />
          </div>
        ))}
      </div>
      <Button size="sm" onClick={save} data-testid="routing-save" className="mt-3 rounded-full bg-charcoal text-xs text-white">Save routing</Button>
    </div>
  );
}

function SettingsManager({ config, setConfig }) {
  const [changeNote, setChangeNote] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const save = async () => {
    try {
      const { data } = await api.put("/admin/config", { ...config, change_note: changeNote });
      setConfig(data); setChangeNote(""); toast.success(`Settings saved (v${data.config_version || "?"})`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const uploadMedia = async (file, field) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("field", field);
    try { const { data } = await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } }); setConfig({ ...config, [field]: data.url }); toast.success("Uploaded"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const fields = [["phone", "Phone"], ["whatsapp", "WhatsApp"], ["email_general", "General Email"], ["email_partners", "Partner Email"], ["email_queries", "Queries Email"], ["email_contracting", "Contracting Email"], ["address", "Address"], ["business_hours", "Business Hours"], ["maps_link", "Google Maps Link"], ["analytics_id", "Analytics ID"], ["meta_pixel_id", "Meta Pixel ID"]];
  return (
    <div className="rounded-xl border border-border bg-white p-6">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-xl font-semibold text-charcoal">Website & Contact Settings</h3>
        <Button variant="outline" size="sm" data-testid="config-history-btn" onClick={() => setShowHistory(true)} className="rounded-full text-xs">
          <History className="mr-1 h-3.5 w-3.5" />Version History{config.config_version ? ` (v${config.config_version})` : ""}
        </Button>
      </div>
      {(() => {
        const c = settingsCompletion(config);
        return (
          <div className={`mt-3 rounded-lg border p-4 ${c.essentialsComplete ? "border-forest/30 bg-forest/5" : "border-saffron/40 bg-saffron/10"}`} data-testid="settings-completion">
            <div className="flex items-center gap-2 font-accent text-sm font-semibold">
              {c.essentialsComplete ? <span className="text-forest">Settings {c.done}/{c.total} complete</span>
                : <><AlertTriangle className="h-4 w-4 text-saffron" /><span className="text-saffron">Contact settings incomplete ({c.done}/{c.total}). Placeholder details are hidden from the public site until real values are entered.</span></>}
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {c.items.map((i) => (
                <span key={i.key} data-testid={`setting-status-${i.key}`} className={`rounded-full px-2.5 py-0.5 text-[11px] ${i.ok ? "bg-forest/15 text-forest" : "bg-maroon/10 text-maroon"}`}>{i.ok ? "✓" : "✗"} {i.label}</span>
              ))}
            </div>
          </div>
        );
      })()}
      <div className="mt-5 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {[["logo_url", "Desktop Logo"], ["mobile_logo_url", "Mobile Logo"], ["favicon_url", "Favicon"], ["social_image_url", "Social-Sharing Image"]].map(([field, label]) => (
          <div key={field}><Label className="font-accent text-xs font-semibold">{label}</Label>
            <div className="mt-1 flex items-center gap-3">
              {config[field] ? <img src={fileUrl(config[field])} alt={label} className="h-12 w-auto max-w-[120px] rounded border border-border bg-cream p-1 object-contain" /> : <div className="flex h-12 w-12 items-center justify-center rounded-full bg-maroon text-cream font-display font-bold">M</div>}
              <label className="flex cursor-pointer items-center gap-1 rounded-full border border-saffron px-3 py-1.5 font-accent text-xs text-saffron hover:bg-saffron/5"><Upload className="h-3 w-3" />Upload<input type="file" accept="image/*" data-testid={`${field}-upload`} className="hidden" onChange={(e) => uploadMedia(e.target.files?.[0], field)} /></label>
            </div>
          </div>
        ))}
      </div>
      <LogoSizePanel config={config} setConfig={setConfig} />
      <SocialLinksEditor links={config.social_links || []} onChange={(v) => setConfig({ ...config, social_links: v })} />
      <NotificationRouting />
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        {fields.map(([k, label]) => (<div key={k} className="space-y-1.5"><Label className="font-accent text-xs font-semibold">{label}</Label><Input data-testid={`config-${k}`} value={config[k] || ""} onChange={(e) => setConfig({ ...config, [k]: e.target.value })} /></div>))}
      </div>
      <div className="mt-6 space-y-1.5">
        <Label className="font-accent text-xs font-semibold">Change summary (optional)</Label>
        <Input data-testid="config-change-note" placeholder="e.g. Updated partner email and address" value={changeNote} onChange={(e) => setChangeNote(e.target.value)} />
      </div>
      <Button onClick={save} data-testid="save-config" className="mt-4 rounded-full bg-saffron font-accent font-semibold text-white hover:bg-saffron-dark">Save Settings</Button>
      <ConfigHistory open={showHistory} onOpenChange={setShowHistory} config={config} setConfig={setConfig} />
    </div>
  );
}

function ConfigHistory({ open, onOpenChange, config, setConfig }) {
  const [revisions, setRevisions] = useState([]);
  const [preview, setPreview] = useState(null);
  const [confirmRestore, setConfirmRestore] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/config/revisions"); setRevisions(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { if (open) { load(); setPreview(null); setConfirmRestore(null); } }, [open, load]);
  const openPreview = async (version) => {
    try { const { data } = await api.get(`/admin/config/revisions/${version}`); setPreview(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doRestore = async (version) => {
    try {
      const { data } = await api.post(`/admin/config/revisions/${version}/restore`);
      const { data: fresh } = await api.get("/config");
      setConfig(fresh); setConfirmRestore(null); onOpenChange(false);
      toast.success(`Restored v${version} as new version v${data.version}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="config-history-dialog">
        <DialogHeader><DialogTitle className="font-display">Global Settings — Version History</DialogTitle></DialogHeader>
        {preview ? (
          <div className="space-y-3">
            <button className="text-xs text-saffron underline" data-testid="config-preview-back" onClick={() => setPreview(null)}>← Back to history</button>
            <div className="rounded-lg border border-border bg-cream/30 p-3 text-sm">
              <div className="mb-2 font-accent font-semibold">Version {preview.version} · {fmt(preview.at)} · {preview.by_name || "—"}</div>
              <p className="mb-2 text-xs text-muted-foreground">Read-only snapshot (secrets excluded). Use Restore from the list to apply.</p>
              <div className="grid gap-1.5 sm:grid-cols-2">
                {Object.entries(preview.snapshot || {}).filter(([k]) => k !== "key" && k !== "config_version").map(([k, v]) => (
                  <div key={k} className="flex gap-2 border-b border-border/50 py-1 text-xs">
                    <span className="min-w-[130px] font-medium text-charcoal">{k}</span>
                    <span className="text-muted-foreground break-all">{typeof v === "object" ? JSON.stringify(v) : String(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto">
            {revisions.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">No revisions yet. Save settings to create the first version.</p> : (
              <Table><TableHeader><TableRow>
                <TableHead>Version</TableHead><TableHead>Changed by</TableHead><TableHead>When</TableHead><TableHead>Summary</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {revisions.map((r) => (
                  <TableRow key={r.version} data-testid={`config-rev-${r.version}`}>
                    <TableCell className="font-medium">v{r.version}{config.config_version === r.version && <Badge className="ml-2 bg-forest text-white">current</Badge>}</TableCell>
                    <TableCell>{r.by_name || "—"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmt(r.at)}</TableCell>
                    <TableCell className="text-xs">{r.note || (r.restored_from ? `Restored from v${r.restored_from}` : "—")}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" data-testid={`config-preview-${r.version}`} onClick={() => openPreview(r.version)} className="text-xs"><Eye className="mr-1 h-3.5 w-3.5" />Preview</Button>
                      <Button variant="outline" size="sm" data-testid={`config-restore-${r.version}`} onClick={() => setConfirmRestore(r)} className="ml-1 rounded-full text-xs"><RotateCcw className="mr-1 h-3.5 w-3.5" />Restore</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody></Table>
            )}
          </div>
        )}
        {confirmRestore && (
          <div className="rounded-lg border border-saffron/40 bg-saffron/10 p-4" data-testid="config-restore-confirm">
            <p className="text-sm text-charcoal">Restore <strong>version v{confirmRestore.version}</strong> ({confirmRestore.by_name || "—"}, {fmt(confirmRestore.at)})? Your current settings will be snapshotted and this revision applied as a new version. Nothing is lost.</p>
            <div className="mt-3 flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setConfirmRestore(null)}>Cancel</Button>
              <Button size="sm" data-testid="config-restore-confirm-btn" onClick={() => doRestore(confirmRestore.version)} className="rounded-full bg-saffron text-white hover:bg-saffron-dark">Yes, restore v{confirmRestore.version}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const LOGO_SIZE_DEFAULTS = { logo_w_desktop: 172, logo_h_desktop: 76, header_h_desktop: 100, logo_w_mobile: 128, logo_h_mobile: 56, header_h_mobile: 72, logo_w_footer: 220 };
const LOGO_SIZE_LIMITS = { logo_w_desktop: [100, 320], logo_h_desktop: [40, 120], header_h_desktop: [72, 140], logo_w_mobile: [80, 200], logo_h_mobile: [36, 90], header_h_mobile: [56, 110], logo_w_footer: [120, 360] };
const LOGO_SIZE_FIELDS = [["logo_w_desktop", "Desktop logo width"], ["logo_h_desktop", "Desktop max height"], ["header_h_desktop", "Desktop header height"], ["logo_w_mobile", "Mobile logo width"], ["logo_h_mobile", "Mobile max height"], ["header_h_mobile", "Mobile header height"], ["logo_w_footer", "Footer logo width"]];

function LogoSizePanel({ config, setConfig }) {
  const val = (k) => config[k] ?? LOGO_SIZE_DEFAULTS[k];
  const setVal = (k, v) => {
    const [min, max] = LOGO_SIZE_LIMITS[k];
    const n = Math.max(min, Math.min(max, parseInt(v) || min));
    setConfig({ ...config, [k]: n });
  };
  const reset = () => setConfig({ ...config, ...LOGO_SIZE_DEFAULTS });
  const logo = config.logo_url ? fileUrl(config.logo_url) : null;
  return (
    <div className="mt-6 rounded-xl border border-border bg-cream/30 p-5" data-testid="logo-size-panel">
      <div className="flex items-center justify-between">
        <h4 className="font-display text-lg font-semibold text-charcoal">Logo Size</h4>
        <Button size="sm" variant="outline" data-testid="logo-size-reset" onClick={reset} className="rounded-full text-xs">Reset to Recommended</Button>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">Aspect ratio is preserved automatically. Changing these values never modifies the stored logo file.</p>
      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <div className="grid gap-3 sm:grid-cols-2">
          {LOGO_SIZE_FIELDS.map(([k, label]) => (
            <div key={k}><Label className="font-accent text-xs font-semibold">{label} (px)</Label>
              <Input type="number" data-testid={`logo-size-${k}`} value={val(k)} onChange={(e) => setVal(k, e.target.value)} min={LOGO_SIZE_LIMITS[k][0]} max={LOGO_SIZE_LIMITS[k][1]} /></div>
          ))}
        </div>
        <div className="space-y-3">
          <div className="rounded-lg border border-border bg-white p-3">
            <div className="overline mb-2 text-[10px] text-muted-foreground">Desktop header preview</div>
            <div className="flex items-center rounded bg-cream/60 px-3" style={{ minHeight: val("header_h_desktop") }}>
              {logo ? <img src={logo} alt="preview" style={{ width: val("logo_w_desktop"), maxHeight: val("logo_h_desktop"), height: "auto" }} className="object-contain" /> : <span className="text-xs text-muted-foreground">Upload a logo to preview</span>}
            </div>
          </div>
          <div className="rounded-lg border border-border bg-white p-3">
            <div className="overline mb-2 text-[10px] text-muted-foreground">Mobile header preview</div>
            <div className="flex items-center rounded bg-cream/60 px-3" style={{ minHeight: val("header_h_mobile") }}>
              {logo ? <img src={logo} alt="preview" style={{ width: val("logo_w_mobile"), maxHeight: val("logo_h_mobile"), height: "auto" }} className="object-contain" /> : <span className="text-xs text-muted-foreground">—</span>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ChangePasswordButton() {
  const [open, setOpen] = useState(false);
  const [cur, setCur] = useState(""); const [nw, setNw] = useState("");
  const submit = async () => { try { await api.post("/admin/change-password", { current_password: cur, new_password: nw }); toast.success("Password changed"); setOpen(false); setCur(""); setNw(""); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  return (
    <>
      <Button size="sm" variant="ghost" data-testid="change-password-btn" onClick={() => setOpen(true)} className="rounded-full"><KeyRound className="h-4 w-4" /></Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="change-password-dialog"><DialogHeader><DialogTitle className="font-display text-xl">Change Password</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-semibold">Current Password</Label><Input data-testid="current-password" type="password" value={cur} onChange={(e) => setCur(e.target.value)} /></div>
            <div><Label className="text-xs font-semibold">New Password (min 8)</Label><Input data-testid="new-password" type="password" value={nw} onChange={(e) => setNw(e.target.value)} /></div>
          </div>
          <DialogFooter><Button data-testid="submit-password" onClick={submit} className="rounded-full bg-saffron text-white hover:bg-saffron-dark">Update</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
